import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../services/api";
import Constants from "../../services/constants";
import { hideLoader, showLoader } from "./loaderSlice";
import { showToast } from "./toastSlice";

// Only persist essential data
const loadState = () => {
  try {
    const serializedState = localStorage.getItem('databaseState');
    if (serializedState === null) {
      return undefined;
    }
    const state = JSON.parse(serializedState);
    // Only load essential fields from storage
    return {
      databases: state.databases || [],
      currentDatabase: state.currentDatabase || null
    };
  } catch (err) {
    return undefined;
  }
};

// Optimize state persistence by only saving essential fields
const saveState = (state) => {
  try {
    // Only persist essential data
    const essentialState = {
      databases: state.databases,
      currentDatabase: state.currentDatabase
    };
    localStorage.setItem('databaseState', JSON.stringify(essentialState));
  } catch (err) {
    // Ignore write errors
  }
};

// Split state into essential and non-essential data
const initialState = {
  ...loadState(),
  data: null,
  deployment: {},
  logs: [],
  status: "",
  deployments: [],
  databases: [],
  backups: {},
  cronJobs: {},
  recentJobs: {},
  metrics: {},
  users: {},
  accessLogs: {},
  queryAnalytics: {},
  currentDatabase: null,
  tlsCertificates: {},
  topology: {}
};

export const getDatabases = createAsyncThunk(
  "databases/list",
  async ({ organization_id }, { dispatch }) => {
    try {
      const res = await api("get", `${Constants.END_POINT.dataBaseDeployment}?organization_id=${organization_id}`);
      if (res?.success && res?.data) {
        dispatch(setDatabases(res.data)); // Use new setDatabases action
        return res.data;
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("Failed to fetch databases.");
    }
  }
);

export const getDatabaseDetails = createAsyncThunk(
  "database/details",
  async ({ id }, { dispatch, getState }) => {
    try {
      const activeOrg = getState()?.org?.active;
      const res = await api("get", `${Constants.END_POINT.dataBaseDeployment}/${id}/details?organization_id=${activeOrg?.id}`);
      if (res?.success && res?.data) {
        dispatch(setDatabaseDetails(res.data));
        return res.data;
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred.");
    } 
  }
);

export const getDatabaseTopology = createAsyncThunk(
  "database/topology",
  async ({ id }, { dispatch, getState }) => {
    try {
      const activeOrg = getState()?.org?.active;
      const res = await api("get", `${Constants.END_POINT.dataBaseDeployment}/${id}/topology?organization_id=${activeOrg?.id}`);
      if (res?.success && res?.data) {
        dispatch(setDatabaseTopology({ id, topology: res.data }));
        return res.data;
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: "Failed to fetch database topology" }));
      return Promise.reject(error);
    }
  }
);




export const getConfigurations = createAsyncThunk(
  "configurations",
  async (data , { dispatch, getState }) => {
    try {
      const activeOrg = getState()?.org?.active;
      const res = await api("get", `${Constants.END_POINT.getConfigurations}?organization_id=${activeOrg?.id}`, {},{},data);
      if (res?.success && res?.data) {
        dispatch(setConfigurations(res?.data));
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred.");
    }
  }
);

export const getDatabaseStatusAction = createAsyncThunk(
  "database/status",
  async ({ deploymentName }, { dispatch, getState }) => {
    try {
      const activeOrg = getState()?.org?.active;
      const res = await api("get", `${Constants.END_POINT.dataBaseDeployment}/status?deploymentName=${deploymentName}&organization_id=${activeOrg?.id}`);
      if (res?.success && res?.data) {
        return { name: deploymentName, status: res.data };
      }
      return Promise.reject(res.message);
    } catch (error) {
      return Promise.reject(error.message);
    }
  }
);

export const databaseDeployment = createAsyncThunk(
  "deployment",
  async ({data}, { dispatch }) => {
    try {
      const payload = {
        name: data.name,
        version: data.version,
        description: data.description,
        username: data.username,
        password: data.password,
        visibility: data.visibility,
        dbName: data.dbName,
        type: data.type,
        instanceType: data.instanceType,
        backup: data.backup,
        backupsToKeep: data.backupsToKeep || 7,
        backupSchedule: data.backupSchedule,
        backupStorageClass: data.backupStorageClass,
        backupStorageSize: data.backupStorageSize,
        ...(data.tls_enabled && {
          tls_enabled: data.tls_enabled,
          tls_secret_name: data.tls_secret_name,
          tls_auth_clients: data.tls_auth_clients
        }),
        user_id: data.user_id,
        organization_id: data.organization_id,
        region: data.region,
        architecture: data.architecture,
      };

      const res = await api("post", Constants.END_POINT.dataBaseDeployment, payload);
      if (res?.success && res?.data) {
        dispatch(setDatabaseDeployment(res?.data));
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error?.response?.data?.message }));
      return Promise.reject(error);
    }
  }
);

// Add the V2 database deployment action that uses Hetzner API
export const databaseDeploymentV2 = createAsyncThunk(
  "deployment_v2",
  async ({data}, { dispatch }) => {
    try {
      const payload = {
        name: data.name,
        description: data.description,
        version: data.version,
        serverType: data.serverType,
        location: data.location,
        dbName: data.dbName,
        username: data.username,
        password: data.password,
        backupEnabled: data.backupEnabled,
        backupFrequency: data.backupFrequency,
        backupRetention: data.backupRetention,
        highAvailability: data.highAvailability,
        monitoring: data.monitoring,
        type: data.type,
        user_id: data.user_id,
        organization_id: data.organization_id,
        deploymentModel: data.deploymentModel
      };

      // Fix the URL to avoid duplication and use the correct endpoint
      const res = await api("post", `database/dedicated?org_id=${data.organization_id}`, payload);
      if (res?.success && res?.data) {
        dispatch(setDatabaseDeployment(res?.data));
        dispatch(showToast({ severity: "success", summary: "Dedicated database server deployment initiated" }));
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error?.response?.data?.message || "Failed to deploy dedicated database" }));
      return Promise.reject(error);
    }
  }
);

export const updateDatabaseConfig = createAsyncThunk(
  "update_config",
  async ({ id, ...config }, { dispatch, getState }) => {
    try {
      const activeOrg = getState()?.org?.active;
      const payload = {
        ...config,
        organization_id: activeOrg?.id
      };
      const res = await api("put", `${Constants.END_POINT.dataBaseDeployment}/${id}/configuration`, payload);
      if (res?.success && res?.data) {
        dispatch(showToast({ severity: "success", summary: "Configuration updated successfully" }));
        return res.data;
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error?.response?.data?.message }));
      return Promise.reject(error);
    } 
  }
);

// Add new action for getting TLS certificates
export const getTLSCertificates = createAsyncThunk(
  "get_tls_certificates",
  async ({ id }, { dispatch, getState }) => {
    try {
      const activeOrg = getState()?.org?.active;
      const res = await api("get", `${Constants.END_POINT.dataBaseDeployment}/${id}/tls-certificates?organization_id=${activeOrg?.id}`);
      if (res?.success && res?.data) {
        return res.data;
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: "Failed to get TLS certificates" }));
      return Promise.reject(error);
    } 
  }
);

// Update the updateSSLConfig action to handle certificates
export const updateSSLConfig = createAsyncThunk(
  "update_ssl",
  async ({ id, enabled }, { dispatch, getState }) => {
    try {
      const activeOrg = getState()?.org?.active;
      const payload = {
        enabled,
        organization_id: activeOrg?.id
      };
      const res = await api("put", `${Constants.END_POINT.dataBaseDeployment}/${id}/ssl`, payload);
      if (res?.success && res?.data) {
        dispatch(showToast({ severity: "success", summary: "SSL configuration updated successfully" }));
        
        // If SSL was enabled, get the certificates
        if (enabled) {
          try {
            const certRes = await dispatch(getTLSCertificates({ id })).unwrap();
            return {
              ...res.data,
              tls_cert: certRes.certificates?.cert,
              tls_key: certRes.certificates?.key,
              tls_ca: certRes.certificates?.ca
            };
          } catch (certError) {
            console.error('Error getting certificates:', certError);
            return res.data;
          }
        }
        
        return res.data;
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: "Failed to update SSL configuration" }));
      return Promise.reject(error);
    }
  }
);

export const getAllDatabaseDeployments = createAsyncThunk(
  "deployments",
  async ({ data }, { dispatch, getState }) => {
    try {
      const activeOrg = getState()?.org?.active;
      const res = await api("get", `${Constants.END_POINT.dataBaseDeployment}?organization_id=${activeOrg?.id}`);
      if (res?.success && res?.data) {
        dispatch(setDatabaseDeployments(res?.data));
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred.");
    }
  }
);

export const getQueryAnalytics = createAsyncThunk(
  "get_query_analytics",
  async ({ id, timeRange = '24h' }, { dispatch, getState }) => {
    try {
      const activeOrg = getState()?.org?.active;
      const res = await api("get", `${Constants.END_POINT.dataBaseDeployment}/${id}/query-analytics?timeRange=${timeRange}&organization_id=${activeOrg?.id}`);
      if (res?.data) {
        dispatch(setQueryAnalytics({ id, analytics: res.data }));
        return res.data;
      } else {
        dispatch(showToast({ severity: "error", summary: res.message || "Failed to fetch query analytics" }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: "Failed to fetch query analytics" }));
      return Promise.reject(error);
    } 
  }
);

export const getAccessLogs = createAsyncThunk(
  "get_access_logs",
  async ({ id, timeRange = '24h' }, { dispatch, getState }) => {
    try {
      const activeOrg = getState()?.org?.active;
      const res = await api("get", `${Constants.END_POINT.dataBaseDeployment}/${id}/access-logs?timeRange=${timeRange}&organization_id=${activeOrg?.id}`);
      if (res?.data) {
        dispatch(setAccessLogs({ id, logs: res.data }));
        return res.data;
      } else {
        dispatch(showToast({ severity: "error", summary: res.message || "Failed to fetch access logs" }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: "Failed to fetch access logs" }));
      return Promise.reject(error);
    } 
  }
);

export const getDatabaseBackups = createAsyncThunk(
  "get_backups",
  async ({ id }, { dispatch, getState }) => {
    try {
      const activeOrg = getState()?.org?.active;
      const res = await api("get", `${Constants.END_POINT.dataBaseDeployment}/${id}/backups?organization_id=${activeOrg?.id}`);
      if (res?.success && res?.data) {
        dispatch(setDatabaseBackups({ 
          id, 
          backups: res.data.backups,
          cronJob: res.data.cronJob,
          recentJobs: res.data.recentJobs
        }));
        return res.data;
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: "Failed to fetch backups" }));
      return Promise.reject(error);
    }
  }
);

export const updateInstanceType = createAsyncThunk(
  "update_instance",
  async ({ id, instanceType }, { dispatch, getState }) => {
    try {
      const activeOrg = getState()?.org?.active;
      const payload = {
        instanceType,
        organization_id: activeOrg?.id
      };
      const res = await api("put", `${Constants.END_POINT.dataBaseDeployment}/${id}/instance`, payload);
      if (res?.success && res?.data) {
        dispatch(showToast({ severity: "success", summary: "Instance type updated successfully" }));
        await dispatch(getAllDatabaseDeployments({}));
        return res.data;
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: "Failed to update instance type" }));
      return Promise.reject(error);
    }
  }
);

export const updateDatabaseBackup = createAsyncThunk(
  "update_backup",
  async ({ id, backup }, { dispatch, getState }) => {
    try {
      const activeOrg = getState()?.org?.active;
      const payload = {
        backup,
        organization_id: activeOrg?.id
      };
      const res = await api("put", `${Constants.END_POINT.dataBaseDeployment}/${id}/backup`, payload);
      if (res?.success && res?.data) {
        dispatch(showToast({ severity: "success", summary: "Backup settings updated successfully" }));
        await dispatch(getDatabaseBackups({ id }));
        return res.data;
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: "Failed to update backup settings" }));
      return Promise.reject(error);
    }
  }
);

export const deleteDatabase = createAsyncThunk(
  "delete_database",
  async ({ id }, { dispatch, getState }) => {
    try {
      const activeOrg = getState()?.org?.active;
      const res = await api("delete", `${Constants.END_POINT.dataBaseDeployment}/${id}?organization_id=${activeOrg?.id}`);
      if (res?.success) {
        dispatch(showToast({ severity: "success", summary: "Database deleted successfully" }));
        window.location.href = '/database';
        return id;
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred.");
    }
  }
);

export const addDatabaseUser = createAsyncThunk(
  "add_user",
  async ({ id, userData }, { dispatch, getState }) => {
    try {
      const activeOrg = getState()?.org?.active;
      const payload = {
        ...userData,
        organization_id: activeOrg?.id
      };
      const res = await api("post", `${Constants.END_POINT.dataBaseDeployment}/${id}/users`, payload);
      if (res?.success && res?.data) {
        dispatch(showToast({ severity: "success", summary: "User added successfully" }));
        return { databaseId: id, user: res.data };
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: "Failed to add user" }));
      return Promise.reject(error);
    } 
  }
);

export const deleteDatabaseUser = createAsyncThunk(
  "delete_user",
  async ({ databaseId, userId }, { dispatch, getState }) => {
    try {
      const activeOrg = getState()?.org?.active;
      const res = await api("delete", `${Constants.END_POINT.dataBaseDeployment}/${databaseId}/users/${userId}?organization_id=${activeOrg?.id}`);
      if (res?.success) {
        dispatch(showToast({ severity: "success", summary: "User deleted successfully" }));
        return { databaseId, userId };
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: "Failed to delete user" }));
      return Promise.reject(error);
    } 
  }
);

export const restoreBackup = createAsyncThunk(
  "restore_backup",
  async ({ id, backup }, { dispatch, getState }) => {
    try {
      const activeOrg = getState()?.org?.active;
      const res = await api("post", `${Constants.END_POINT.dataBaseDeployment}/${id}/backups/rebuild`, {
        backupFile: backup,
        organization_id: activeOrg?.id
      });
      if (res?.success) {
        dispatch(showToast({ 
          severity: "success", 
          summary: "Database restore initiated",
          persistent: true // Add to notifications panel as well
        }));
        await dispatch(getDatabaseBackups({ id }));
        return res.data;
      } else {
        dispatch(showToast({ 
          severity: "error", 
          summary: res.message,
          persistent: true // Important error should be added to notifications
        }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ 
        severity: "error", 
        summary: error.message || "Failed to restore backup",
        persistent: true // Important error should be added to notifications
      }));
      return Promise.reject(error);
    }
  }
);

export const triggerManualBackup = createAsyncThunk(
  "trigger_manual_backup",
  async ({ id, name, type }, { dispatch, getState }) => {
    try {
      const activeOrg = getState()?.org?.active;
      const payload = {
        name,
        type,
        organization_id: activeOrg?.id
      };
      const res = await api("post", `${Constants.END_POINT.dataBaseDeployment}/${id}/backups/manual`, payload);
      if (res?.success) {
        dispatch(showToast({ 
          severity: "success", 
          summary: "Manual backup initiated",
          persistent: true // Add to notifications panel as well
        }));
        await dispatch(getDatabaseBackups({ id }));
        return res.data;
      } else {
        dispatch(showToast({ 
          severity: "error", 
          summary: res.message,
          persistent: true // Important error should be added to notifications
        }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ 
        severity: "error", 
        summary: error.message || "Failed to trigger manual backup",
        persistent: true // Important error should be added to notifications
      }));
      return Promise.reject(error);
    }
  }
);

export const fetchRootPassword = createAsyncThunk(
  "fetch_root_password",
  async ({ id }, { dispatch, getState }) => {
    try {
      const activeOrg = getState()?.org?.active;
      dispatch(showLoader());
      const res = await api("get", `${Constants.END_POINT.dataBaseDeployment}/${id}/root-password?organization_id=${activeOrg?.id}`);
      dispatch(hideLoader());
      if (res?.success && res?.data) {
        return res.data;
      } else {
        dispatch(showToast({ severity: "error", summary: res.message || "Failed to fetch root password" }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(hideLoader());
      dispatch(showToast({ severity: "error", summary: error.message || "Failed to fetch root password" }));
      return Promise.reject(error);
    }
  }
);

const databaseSlice = createSlice({
  name: "configurations",
  initialState,
  reducers: {
    setConfigurations: (state, action) => {
      state.data = action.payload;
    },
    setDatabases: (state, action) => {
      state.databases = action.payload;
      // Only persist when database list changes
      saveState(state);
    },
    setDatabaseDeployment: (state, action) => {
      state.deployment = action.payload;
      saveState(state);
    },
    setDatabaseDeploymentLogs: (state, action) => {
      state.logs = action.payload || [];
    },
    setDatabaseStatus: (state, action) => {
      state.status = action.payload;
    },
    setDatabaseDeployments: (state, action) => {
      state.deployments = action.payload;
    },
    setDatabaseBackups: (state, action) => {
      const { id, backups, cronJob, recentJobs } = action.payload;
      state.backups = {
        ...state.backups,
        [id]: backups
      };
      if (cronJob) {
        state.cronJobs = {
          ...state.cronJobs,
          [id]: cronJob
        };
      }
      if (recentJobs) {
        state.recentJobs = {
          ...state.recentJobs,
          [id]: recentJobs
        };
      }
    },
    setDatabaseMetrics: (state, action) => {
      const { id, metrics } = action.payload;
      state.metrics = {
        ...state.metrics,
        [id]: metrics
      };
    },
    setDatabaseUsers: (state, action) => {
      const { id, users } = action.payload;
      state.users = {
        ...state.users,
        [id]: users
      };
    },
    setAccessLogs: (state, action) => {
      const { id, logs } = action.payload;
      state.accessLogs = {
        ...state.accessLogs,
        [id]: logs
      };
    },
    setQueryAnalytics: (state, action) => {
      const { id, analytics } = action.payload;
      state.queryAnalytics = {
        ...state.queryAnalytics,
        [id]: analytics
      };
    },
    setDatabaseDetails: (state, action) => {
      state.currentDatabase = action.payload;
      saveState(state);
    },
    setTLSCertificates: (state, action) => {
      const { id, certificates } = action.payload;
      state.tlsCertificates = {
        ...state.tlsCertificates,
        [id]: certificates
      };
    },
    setDatabaseTopology: (state, action) => {
      const { id, topology } = action.payload;
      state.topology = {
        ...state.topology,
        [id]: topology
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDatabases.fulfilled, (state, action) => {
        state.databases = action.payload;
        saveState(state);
      })
      .addCase(deleteDatabaseUser.fulfilled, (state, action) => {
        const { databaseId, userId } = action.payload;
        if (state.users[databaseId]) {
          state.users[databaseId] = state.users[databaseId].filter(
            user => user.id !== userId
          );
        }
        saveState(state);
      })
      .addCase(updateDatabaseConfig.fulfilled, (state, action) => {
        const updatedDatabase = action.payload;
        if (updatedDatabase) {
          state.deployments = state.deployments.map(db =>
            db.id === updatedDatabase.id ? { ...db, ...updatedDatabase } : db
          );
          if (state.currentDatabase?.id === updatedDatabase.id) {
            state.currentDatabase = { ...state.currentDatabase, ...updatedDatabase };
          }
          saveState(state);
        }
      })
      .addCase(updateInstanceType.fulfilled, (state, action) => {
        const updatedDatabase = action.payload;
        if (updatedDatabase) {
          state.deployments = state.deployments.map(db =>
            db.id === updatedDatabase.id ? { ...db, ...updatedDatabase } : db
          );
          if (state.currentDatabase?.id === updatedDatabase.id) {
            state.currentDatabase = { ...state.currentDatabase, ...updatedDatabase };
          }
          saveState(state);
        }
      })
      .addCase(getDatabaseStatusAction.fulfilled, (state, action) => {
        const { name, status } = action.payload;
        state.deployments = state.deployments.map(db =>
          db.name === name ? { ...db, status } : db
        );
        saveState(state);
      })
      .addCase(getDatabaseDetails.fulfilled, (state, action) => {
        state.currentDatabase = action.payload;
        saveState(state);
      })
      .addCase(getTLSCertificates.fulfilled, (state, action) => {
        const { id, certificates } = action.payload;
        if (certificates) {
          state.tlsCertificates[id] = certificates;
          saveState(state);
        }
      })
      .addCase(updateSSLConfig.fulfilled, (state, action) => {
        const updatedDatabase = action.payload;
        if (updatedDatabase) {
          state.deployments = state.deployments.map(db =>
            db.id === updatedDatabase.id ? { ...db, ...updatedDatabase } : db
          );
          
          if (state.currentDatabase?.id === updatedDatabase.id) {
            state.currentDatabase = {
              ...state.currentDatabase,
              ...updatedDatabase
            };
          }
          
          if (updatedDatabase.tls_cert && updatedDatabase.tls_key && updatedDatabase.tls_ca) {
            state.tlsCertificates[updatedDatabase.id] = {
              cert: updatedDatabase.tls_cert,
              key: updatedDatabase.tls_key,
              ca: updatedDatabase.tls_ca
            };
          } else {
            delete state.tlsCertificates[updatedDatabase.id];
          }
          saveState(state);
        }
      });
  }
});

export const {
  setConfigurations,
  setDatabases, // Add new action
  setDatabaseDeployment,
  setDatabaseDeploymentLogs,
  setDatabaseStatus,
  setDatabaseDeployments,
  setDatabaseBackups,
  setDatabaseMetrics,
  setDatabaseUsers,
  setAccessLogs,
  setQueryAnalytics,
  setDatabaseDetails,
  setTLSCertificates,
  setDatabaseTopology
} = databaseSlice.actions;

export default databaseSlice.reducer;
